import { Component, EventEmitter, HostListener, Inject, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController, Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { AccountPaymentComponent } from '../account-payment/account-payment.component';
import { PaymentService } from '../services/payment.service';
import { SubscriptionService } from '../services/subscription.service';
import { ToggleService } from '../services/toggle.service';
import { UserService } from '../services/user.service';
import { scrollFocusedFieldIntoView } from '../utils/scroll-focused-field';
import {
  getSubscriptionSubmitAction,
  resolveSubscriptionApiMethod,
  hasExpirationDate,
  isExpirationApproaching,
  isExpirationWithinWindow,
  isSubscriptionCanceled,
  isSubscriptionPastDue,
  shouldShowPaymentSection,
  SubscriptionSubmitAction,
} from '../utils/subscription-billing.util';
import { SubscriptionHistoryComponent } from './subscription-history.component';

export interface PaymentMethodRow {
  token: string;
  default: boolean;
  masked: string;
  cardType?: string;
  expiration?: string;
}

export interface TransactionRow {
  id: string;
  amount: string;
  status: string;
  type: string;
  createdAt: string;
  currencyIsoCode?: string;
}

@Component({
  selector: 'app-account-settings-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class AccountSettingsPaymentComponent implements OnInit {
  @Output() pending = new EventEmitter<Object>();

  paymentFormGroup: UntypedFormGroup;
  type: string;
  expiration: string | null = null;
  isExpired = false;
  updatePending = false;
  formLoaded = false;
  existingSubscriptionId: string | false = false;
  subscriptionStatus: string | null = null;
  firstBillingDate: string | null = null;
  paidThroughDate: string | null = null;
  paymentMethods: PaymentMethodRow[] = [];
  transactions: TransactionRow[] = [];
  transactionsLoading = false;
  showPaymentMethodsSection = false;
  showTransactionHistorySection = false;
  fieldLabelPlacement: 'stacked' | 'fixed' = 'fixed';

  private transactionsLoaded = false;

  private account_id: number;

  @ViewChild('paymentsForm', { read: AccountPaymentComponent }) paymentForm: AccountPaymentComponent;

  constructor(
    private paymentService: PaymentService,
    private subscriptionService: SubscriptionService,
    private toggleService: ToggleService,
    private userService: UserService,
    private alertController: AlertController,
    private modalController: ModalController,
    private toastController: ToastController,
    private translate: TranslateService,
    private platform: Platform,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('canAdminister') public canAdminister: boolean,
  ) {}

  ngOnInit(): void {
    this.syncFieldLabelPlacement();
    if (!this.accountInformation) {
      return;
    }
    this.account_id = this.accountInformation.general.external_id;
    this.type = this.accountInformation?.general?.type ?? 'N/A';
    this.loadBillingData();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncFieldLabelPlacement();
  }

  get isSmallScreenLayout(): boolean {
    const w = this.platform?.width() ?? 0;
    const isDevice =
      this.platform.is('mobile') ||
      this.platform.is('tablet') ||
      this.platform.is('phablet') ||
      this.platform.is('mobileweb');
    return w < 875 && !!isDevice;
  }

  scrollFormFieldIntoView(event: FocusEvent): void {
    scrollFocusedFieldIntoView(event, { enabled: this.isSmallScreenLayout });
  }

  isToggleEnabled(toggleName: string): boolean {
    return this.toggleService.isToggleEnabled(toggleName);
  }

  accountExpired(): boolean {
    return this.userService.accountExpired();
  }

  subscriptionLapsed(): boolean {
    return this.isExpired || this.accountExpired();
  }

  subscriptionOptionsEnabled(): boolean {
    return this.isToggleEnabled('freeToPremium');
  }

  shouldShowBillingContent(): boolean {
    return (
      this.subscriptionOptionsEnabled() &&
      shouldShowPaymentSection({
        accountType: this.type,
        expiration: this.expiration,
        accountExpired: this.accountExpired(),
        existingSubscriptionId: this.existingSubscriptionId,
      })
    );
  }

  shouldShowStartSubscription(): boolean {
    return (
      this.subscriptionOptionsEnabled() &&
      this.type === 'Free'
    );
  }

  shouldShowRenewSubscription(): boolean {
    return (
      this.subscriptionOptionsEnabled() &&
      this.type !== 'Free' &&
      this.shouldShowBillingContent() &&
      isExpirationWithinWindow(this.expiration) &&
      this.subscriptionLapsed()
    );
  }

  /** Braintree drop-in for adding or updating cards whenever billing is available. */
  shouldShowPaymentEditor(): boolean {
    return this.shouldShowBillingContent() && !!this.canAdminister;
  }

  /** Start / renew / cancel subscription controls — only within the renewal window. */
  shouldShowSubscriptionActions(): boolean {
    return this.shouldShowBillingContent() && isExpirationWithinWindow(this.expiration);
  }

  shouldShowPaymentManagement(): boolean {
    return this.shouldShowSubscriptionActions();
  }

  shouldShowCancelSubscription(): boolean {
    return (
      !!this.canAdminister &&
      !!this.existingSubscriptionId &&
      !isSubscriptionCanceled(this.subscriptionStatus) &&
      this.shouldShowPaymentManagement()
    );
  }

  get submitAction(): SubscriptionSubmitAction {
    return getSubscriptionSubmitAction({
      existingSubscriptionId: this.existingSubscriptionId,
      subscriptionStatus: this.subscriptionStatus,
    });
  }

  get submitButtonLabelKey(): string {
    switch (this.submitAction) {
      case 'create':
        return 'GENERAL.CREATE_SUBSCRIPTION';
      case 'resubscribe':
        return 'GENERAL.RESUBSCRIBE';
      case 'update':
        if (isSubscriptionPastDue(this.subscriptionStatus)) {
          return 'GENERAL.RESUBSCRIBE';
        }
        return 'GENERAL.UPDATE';
      default:
        return 'GENERAL.SUBMIT';
    }
  }

  private get subscriptionPersistMethod(): 'create' | 'update' | null {
    return resolveSubscriptionApiMethod({
      existingSubscriptionId: this.existingSubscriptionId,
      subscriptionStatus: this.subscriptionStatus,
      subscriptionLapsed: this.subscriptionLapsed(),
    });
  }

  shouldPersistSubscription(): boolean {
    return (
      this.shouldShowStartSubscription() ||
      this.shouldShowRenewSubscription() ||
      this.subscriptionPersistMethod != null
    );
  }

  canStartSubscription(): boolean {
    if (!this.canAdminister || !this.shouldShowStartSubscription()) {
      return false;
    }
    if (!this.paymentFormGroup?.valid) {
      return false;
    }
    return this.hasRequestablePaymentMethod();
  }

  canSubscribe(): boolean {
    if (!this.canAdminister || !this.shouldShowRenewSubscription()) {
      return false;
    }
    if (!this.paymentFormGroup?.valid) {
      return false;
    }
    return this.hasRequestablePaymentMethod();
  }

  canResubscribe(): boolean {
    if (!this.canAdminister || this.submitAction !== 'resubscribe') {
      return false;
    }
    if (!this.paymentFormGroup?.valid) {
      return false;
    }
    return this.hasRequestablePaymentMethod();
  }

  canUpdatePastDueSubscription(): boolean {
    if (!this.canAdminister || !isSubscriptionPastDue(this.subscriptionStatus)) {
      return false;
    }
    if (!this.paymentFormGroup?.valid) {
      return false;
    }
    return this.hasRequestablePaymentMethod();
  }

  private hasRequestablePaymentMethod(): boolean {
    if (this.paymentForm) {
      return this.paymentForm.isValid();
    }
    return this.paymentMethods.length > 0;
  }

  canSubmit(): boolean {
    if (!this.canAdminister) {
      return false;
    }
    if (this.submitAction === 'create' || this.shouldShowStartSubscription()) {
      return this.canStartSubscription();
    }
    if (this.submitAction === 'resubscribe') {
      return this.canResubscribe();
    }
    if (isSubscriptionPastDue(this.subscriptionStatus)) {
      return this.canUpdatePastDueSubscription();
    }
    if (this.shouldShowRenewSubscription()) {
      return this.canSubscribe();
    }
    return this.isValid();
  }

  isValid(): boolean {
    const f = this.paymentFormGroup;
    if (!f || !f.valid) {
      return false;
    }
    if (this.submitAction === 'resubscribe') {
      return this.canResubscribe();
    }
    if (isSubscriptionPastDue(this.subscriptionStatus)) {
      return this.canUpdatePastDueSubscription();
    }
    if (this.shouldShowRenewSubscription()) {
      return this.canSubscribe();
    }
    if (this.submitAction === 'create' || this.shouldShowStartSubscription()) {
      return this.canStartSubscription();
    }
    return true;
  }

  get expirationDisplay(): string {
    const source = this.paidThroughDate || this.expiration;
    return this.formatDisplayDate(source);
  }

  get firstBillingDateDisplay(): string {
    return this.formatDisplayDate(this.firstBillingDate);
  }

  get subscriptionStatusDisplay(): string {
    if (!this.subscriptionStatus) {
      return this.translate.instant('GENERAL.NOT_AVAILABLE');
    }
    return this.subscriptionStatus;
  }

  get hasSubscriptionDetails(): boolean {
    return !!this.existingSubscriptionId;
  }

  get shouldShowPrimarySubmit(): boolean {
    if (!this.canAdminister) {
      return false;
    }
    return (
      this.shouldShowStartSubscription() ||
      this.shouldShowRenewSubscription() ||
      this.shouldShowPaymentEditor() ||
      this.shouldPersistSubscription()
    );
  }

  get paymentMethodsSummary(): string {
    if (!this.paymentMethods.length) {
      return this.translate.instant('PAYMENT.NO_PAYMENT_METHODS');
    }
    const defaultMethod = this.paymentMethods.find((m) => m.default) || this.paymentMethods[0];
    if (this.paymentMethods.length === 1) {
      return defaultMethod.masked;
    }
    return this.translate.instant('PAYMENT.METHODS_ON_FILE', { count: this.paymentMethods.length });
  }

  get transactionsSummary(): string {
    if (!this.transactionsLoaded || this.transactionsLoading) {
      return '';
    }
    if (!this.transactions.length) {
      return this.translate.instant('PAYMENT.NO_TRANSACTIONS');
    }
    return this.translate.instant('PAYMENT.TRANSACTIONS_COUNT', { count: this.transactions.length });
  }

  togglePaymentMethodsSection(): void {
    this.showPaymentMethodsSection = !this.showPaymentMethodsSection;
  }

  toggleTransactionHistorySection(): void {
    this.showTransactionHistorySection = !this.showTransactionHistorySection;
    if (this.showTransactionHistorySection && !this.transactionsLoaded) {
      this.transactionsLoaded = true;
      void this.loadTransactions();
    }
  }

  async openSubscriptionHistory(): Promise<void> {
    const modal = await this.modalController.create({
      component: SubscriptionHistoryComponent,
      componentProps: { accountId: this.account_id },
    });
    await modal.present();
  }

  /** Stacked labels in the subscription block avoid truncated fixed labels. */
  readonly subscriptionFieldLabelPlacement: 'stacked' = 'stacked';

  private formatDisplayDate(value: string | null | undefined): string {
    if (!value) {
      return this.translate.instant('GENERAL.NOT_AVAILABLE');
    }
    const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (!parsed.isValid()) {
      const fallback = moment(value);
      return fallback.isValid() ? fallback.format('MMM DD, YYYY') : value;
    }
    return parsed.format('MMM DD, YYYY');
  }

  private applySubscriptionDetails(subscription: any): void {
    if (!subscription) {
      this.existingSubscriptionId = false;
      this.subscriptionStatus = null;
      this.firstBillingDate = null;
      this.paidThroughDate = null;
      return;
    }

    this.existingSubscriptionId = subscription['id'] ? String(subscription['id']) : false;
    this.subscriptionStatus = subscription['status'] ? String(subscription['status']) : null;
    this.firstBillingDate = this.normalizeDateString(subscription['firstBillingDate']);
    this.paidThroughDate =
      this.normalizeDateString(subscription['paidThroughDate']) ||
      this.normalizeDateString(subscription['expirationDate']);

    const expiration =
      this.paidThroughDate ||
      this.accountInformation?.general?.expiration ||
      null;
    this.setExpirationFromSource(expiration);
    this.syncPaymentMethodsSectionVisibility();
  }

  private syncPaymentMethodsSectionVisibility(): void {
    if (
      this.submitAction === 'resubscribe' ||
      this.submitAction === 'create' ||
      isSubscriptionPastDue(this.subscriptionStatus)
    ) {
      this.showPaymentMethodsSection = true;
    }
  }

  private normalizeDateString(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }
    const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }
    const fallback = moment(String(value));
    return fallback.isValid() ? fallback.format('YYYY-MM-DD') : null;
  }

  startSubscription(): void {
    void this.save();
  }

  subscribe(): void {
    void this.save();
  }

  startEditing(forcePayment = false): void {
    if (forcePayment && this.paymentForm) {
      this.paymentForm.showPaymentForm();
    }
  }

  async save(): Promise<void> {
    if (!this.canAdminister) {
      return;
    }
    try {
      this.updatePending = true;

      if (this.shouldPersistSubscription()) {
        let paymentReady = !!this.paymentService.getNonce();
        if (this.paymentForm) {
          paymentReady = await this.paymentForm.save();
        }
        if (!paymentReady) {
          throw new Error('Payment method required');
        }

        const formValue = this.paymentFormGroup.value;
        let subscriptionResponse;
        const persistMethod = this.subscriptionPersistMethod;
        if (persistMethod === 'update' && this.existingSubscriptionId) {
          subscriptionResponse = await this.subscriptionService.update(
            this.account_id,
            this.existingSubscriptionId,
            formValue,
          );
        } else if (persistMethod === 'create') {
          subscriptionResponse = await this.subscriptionService.create(this.account_id, formValue);
        } else {
          throw new Error('No subscription change to save');
        }
        this.paymentService.clearNonce();
        const refreshed = await this.subscriptionService.get(this.account_id);
        this.applySubscriptionDetails(refreshed || subscriptionResponse);
      } else if (this.paymentForm) {
        const paymentSaved = await this.paymentForm.save();
        if (paymentSaved) {
          this.paymentService.clearNonce();
        }
      }

      await this.loadPaymentMethods();
      if (this.transactionsLoaded) {
        await this.loadTransactions();
      }
      this.updatePending = false;
      this.pending.emit({
        is_pending: false,
        message: this.translate.instant('PAYMENT.UPDATE_SUCCESS'),
        success: true,
      });
    } catch (e) {
      this.updatePending = false;
      this.pending.emit({
        is_pending: false,
        message: this.translate.instant('PAYMENT.UPDATE_FAILURE'),
        success: false,
      });
    }
  }

  async cancelSubscription(): Promise<void> {
    if (!this.existingSubscriptionId) {
      return;
    }
    const alert = await this.alertController.create({
      header: this.translate.instant('GENERAL.CANCEL_SUBSCRIPTION_CONFIRM_HEADER'),
      message: this.translate.instant('GENERAL.CANCEL_SUBSCRIPTION_CONFIRM_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('GENERAL.KEEP_SUBSCRIPTION'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translate.instant('GENERAL.CANCEL_SUBSCRIPTION'),
          handler: () => {
            void (async () => {
              await this.subscriptionService.delete(this.account_id, this.existingSubscriptionId);
              const refreshed = await this.subscriptionService.get(this.account_id);
              this.applySubscriptionDetails(refreshed);
            })();
          },
        },
      ],
    });
    await alert.present();
  }

  formatTransactionDate(value: string): string {
    if (!value) {
      return '';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : value;
  }

  formatTransactionAmount(tx: TransactionRow): string {
    const currency = tx.currencyIsoCode || 'USD';
    const amount = tx.amount != null ? Number(tx.amount).toFixed(2) : '0.00';
    return `${currency === 'USD' ? '$' : ''}${amount}${currency !== 'USD' ? ` ${currency}` : ''}`;
  }

  private syncFieldLabelPlacement(): void {
    this.fieldLabelPlacement = this.platform.width() < 720 ? 'stacked' : 'fixed';
  }

  private setExpirationFromSource(expiration: string | Date | null | undefined): void {
    if (expiration == null || expiration === '') {
      this.expiration = null;
      this.isExpired = false;
      return;
    }
    const exp = moment(expiration);
    if (!exp.isValid()) {
      this.expiration = null;
      this.isExpired = false;
      return;
    }
    this.expiration = exp.format('YYYY-MM-DD');
    this.isExpired = moment().startOf('day').isAfter(exp.startOf('day')) || this.accountExpired();
  }

  private loadBillingData(): void {
    this.formLoaded = false;
    const setUpWithoutSubscription = () => {
      this.createForm();
      const expiration =
        this.accountInformation?.general?.expiration ?? null;
      this.setExpirationFromSource(expiration);
      void this.loadPaymentMethods();
    };

    if (this.isToggleEnabled('freeToPremium')) {
      this.subscriptionService
        .get(this.account_id)
        .then((subscription) => {
          this.applySubscriptionDetails(subscription);
          this.createForm(subscription);
          void this.loadPaymentMethods();
        })
        .catch(() => {
          setUpWithoutSubscription();
        });
    } else {
      setUpWithoutSubscription();
    }
  }

  private createForm(subscription: any = undefined): void {
    if (!this.accountInformation || this.paymentFormGroup) {
      return;
    }
    let freq = 'kiln_annual';
    let diffFreq = 1;
    if (subscription) {
      freq = subscription.planId || 'kiln_annual';
      const difference = moment.duration(
        moment(subscription.paidThroughDate).diff(moment(subscription.firstBillingDate)),
      );
      diffFreq = Math.round(freq.includes('annual') ? difference.asYears() : difference.asMonths());
    }
    const kilnLimit = this.accountInformation?.general?.kiln_limit ?? 1;
    this.paymentFormGroup = new UntypedFormGroup({
      numberControllers: new UntypedFormControl(kilnLimit > 0 ? kilnLimit : 1, [Validators.required, Validators.min(1)]),
      frequency: new UntypedFormControl(freq, [Validators.required]),
      renewals: new UntypedFormControl(diffFreq || 1, [Validators.required]),
    });
    this.formLoaded = true;
  }

  private async loadPaymentMethods(): Promise<void> {
    try {
      const response = await this.paymentService.getPaymentInfo(this.account_id);
      this.paymentMethods = response?.paymentMethods ?? (response?.payment ? [response.payment] : []);
    } catch {
      this.paymentMethods = [];
    }
  }

  private async loadTransactions(): Promise<void> {
    this.transactionsLoading = true;
    try {
      const response = await this.paymentService.getTransactions(this.account_id);
      this.transactions = response?.transactions ?? [];
    } catch {
      this.transactions = [];
    } finally {
      this.transactionsLoading = false;
    }
  }
}
